import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import { markNewsAsRead, markNewsSeen } from "../../../containers/dashboard/dashboardSlice";

/**
 * Read/received acknowledgement for a news item, shown at the bottom of the
 * news detail modal — acknowledging requires actually opening the message.
 *
 * Viewing no longer marks it as read: the collab has to tick this box
 * explicitly. The acknowledgement is irreversible — once ticked the box is
 * locked (the backend also refuses to overwrite an existing `news_collabs` row).
 *
 * @param {object} news the news item (only `id` is required, state is read from the store)
 */
export default function NewsReadCheckbox({ news }) {

    const dispatch = useDispatch();
    const newsList = useSelector((state) => state.dashboard.news);
    const [submitting, setSubmitting] = useState(false);

    // `news` can be a snapshot taken when the row was clicked, so resolve the
    // live item from the store to stay in sync after an acknowledgement.
    const item = newsList?.find((n) => n.id === news?.id) || news;
    const seen = item?.seen;

    const handleChange = async (event) => {
        if (!event.target.checked || seen || submitting || !item?.id) return;
        setSubmitting(true);
        try {
            const response = await dispatch(markNewsAsRead({ newsId: item.id })).unwrap();
            if (response?.responseData?.status) {
                dispatch(markNewsSeen({
                    newsId: item.id,
                    seen: moment().format("YYYY-MM-DD HH:mm:ss"),
                }));
            } else {
                showErrorToast(Localize("NEWS_MARK_AS_READ_FAILED"));
            }
        } catch (error) {
            showErrorToast(Localize("NEWS_MARK_AS_READ_FAILED"));
        } finally {
            setSubmitting(false);
        }
    };

    const acknowledgedOn = seen
        ? Localize("NEWS_ACKNOWLEDGED_ON").replace("%date%", moment(seen).format("DD/MM/YYYY HH:mm"))
        : null;

    const checkbox = (
        <Checkbox
            checked={Boolean(seen)}
            disabled={Boolean(seen) || submitting}
            onChange={handleChange}
            inputProps={{ "aria-label": Localize("NEWS_MARK_AS_READ") }}
            sx={{
                color: "#FF9015",
                "&.Mui-checked": { color: "#2e7d32" },
                "&.Mui-disabled": { color: seen ? "#2e7d32" : undefined },
            }}
        />
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                p: 1.5,
                borderRadius: "12px",
                border: seen ? "1px solid rgba(46, 125, 50, 0.24)" : "1px solid rgba(255, 144, 21, 0.24)",
                backgroundColor: seen ? "rgba(46, 125, 50, 0.06)" : "rgba(255, 144, 21, 0.06)",
            }}
        >
            <FormControlLabel
                control={submitting ? <CircularProgress size={20} sx={{ mx: "10px" }} /> : checkbox}
                label={
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                        <Langs str="NEWS_MARK_AS_READ" />
                    </Typography>
                }
                sx={{ m: 0 }}
            />
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    color: seen ? "#2e7d32" : "#6b6560",
                    pl: "34px",
                }}
            >
                {seen ? acknowledgedOn : <Langs str="NEWS_MARK_AS_READ_IRREVERSIBLE" />}
            </Typography>
        </Box>
    );
}
